import { useCallback, useEffect, useState } from "react";

import type { ResourceActivityEntryUI } from "@domains/resource/types";
import { useApi } from "@platform/hooks/useApi";

interface UseProjectActivityReturn {
  activity: ResourceActivityEntryUI[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetch recent activity across all resources in a project.
 * Backed by GET /api/v1/projects/:alias/activity?limit=5.
 * Used by the Project Overview sidebar.
 */
export function useProjectActivity(
  projectAlias: string | undefined,
  limit = 5,
): UseProjectActivityReturn {
  const api = useApi();
  const [activity, setActivity] = useState<ResourceActivityEntryUI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!projectAlias) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<{
        entries: ResourceActivityEntryUI[];
        total: number;
      }>(
        `/api/v1/projects/${encodeURIComponent(projectAlias)}/activity?limit=${limit}`,
        {
          errorMessage: `Failed to load activity for project: ${projectAlias}`,
          showLoading: false,
        },
      );
      setActivity(result?.entries ?? []);
    } catch {
      setError("Failed to load activity.");
      setActivity([]);
    } finally {
      setLoading(false);
    }
  }, [projectAlias, limit]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { activity, loading, error, reload };
}
